import { AlertCircle, Brain, Crown, FileText, LifeBuoy, LogIn, LogOut, Settings } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { OPERATOR } from 'legal';
import { useCurrentUser } from '@/entities/user';
import { ROUTES } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import {
  DesktopRootsContext,
  DesktopShellContext,
  useDesktopLayout,
} from '@/shared/lib/desktopLayout';
import { openTelegram } from '@/shared/lib/telegram';
import { useFullWidthPage } from '@/shared/lib/useFullWidthPage';
import { logout } from '@/shared/lib/webSession';
import { Button } from '@/shared/ui';
import { TABS } from '@/widgets/main-tabs';

/**
 * Пункты профиля — на компьютере прямо в сайдбаре, а не отдельной страницей
 * «Профиль»: аватар с именем и кнопка премиума в сайдбаре уже есть, а
 * страница была бы списком ссылок на эти же экраны (решение Дмитрия,
 * 2026-10-26). Тексты — из словаря `ProfilePage`. На телефоне — по-старому,
 * через вкладку «Профиль».
 */
const ACCOUNT_LINKS = [
  { to: ROUTES.subscription, labelKey: 'subscription', icon: Crown, premiumOnly: true },
  { to: ROUTES.settings, labelKey: 'settings', icon: Settings },
  // Поддержка — чат, а не экран: кнопка, а не ссылка. Тот же контакт, что в «Документах».
  { support: true, labelKey: 'support', icon: LifeBuoy },
  { to: ROUTES.documents, labelKey: 'documents', icon: FileText },
] as const;

const MAIN_TABS = TABS.filter((tab) => tab.to !== ROUTES.profile);

/** Над линией, вместе с разделами: это про учёбу, а не про аккаунт. */
const LEARNING_LINKS = [
  { to: ROUTES.mistakes, labelKey: 'mistakes', icon: AlertCircle },
  { to: ROUTES.personalization, labelKey: 'personalization', icon: Brain },
] as const;

/** Разделы сайдбара — на них в каркасе нет «← Назад». */
const ROOTS: ReadonlySet<string> = new Set([
  ...MAIN_TABS.map((tab) => tab.to),
  ...LEARNING_LINKS.map((link) => link.to),
  ...ACCOUNT_LINKS.flatMap((link) => ('to' in link ? [link.to] : [])),
]);

const NAV_ITEM =
  'flex items-center gap-3.5 rounded-2xl px-4 py-2.5 text-[15px] font-medium transition-colors';

function navItemClass({ isActive }: { isActive: boolean }): string {
  return cn(NAV_ITEM, isActive ? 'bg-bg text-signal-blue' : 'text-ink hover:bg-bg');
}

/** Зазор над и под колонкой — колонка лежит на белом фоне карточкой с видимыми краями. */
const COLUMN_GAP_PX = 24;

/**
 * Колонка — опора для всех `position: fixed` внутри страниц (шапки, поле
 * ввода чата, полноэкранные экраны): `transform` делает её содержащим блоком,
 * как `body` в колонке телефона (`app/styles/index.css`). Страницы не знают,
 * что они на компьютере, — встают в колонку сами.
 *
 * Страницы высотой во весь экран (`h-dvh`) и резервируют сверху полосу
 * `--status-bar-top` под системный вырез. Здесь эта полоса — зазор над
 * колонкой: переменные переопределены, а `clip-path` срезает её и
 * скругляет верхние углы.
 */
const COLUMN_STYLE = {
  transform: 'translateZ(0)',
  clipPath: `inset(${COLUMN_GAP_PX}px 0 ${COLUMN_GAP_PX}px 0 round 24px)`,
  '--status-bar-top': `${COLUMN_GAP_PX}px`,
  '--safe-top': `${COLUMN_GAP_PX}px`,
  // Снизу так же: всё, что страницы прижимают к низу с отступом
  // `--safe-bottom` (кнопки, поле ввода чата, плашка словаря), встаёт над
  // срезанным зазором, а не под ним.
  '--safe-bottom': `${COLUMN_GAP_PX}px`,
  // Шапка страницы на компьютере — заголовок слева, крупнее и с воздухом
  // (`PageHeader`), ей нужна полоса выше телефонной.
  '--header-band-height': '72px',
  // Нижних табов в каркасе нет — отступы «над таб-баром» (низ страниц,
  // плашка «Повторять/Учить» в словаре) прижимаются к низу колонки.
  '--tabbar-height': '0px',
} as CSSProperties;

/**
 * Веб-версия на компьютере: сайдбар с разделами, страница в колонке, «Выйти»
 * справа от неё (по референсу — веб-кабинет Сбера, 2026-10-26). Всё это —
 * одна группа по центру окна, а не растяжка по краям: на широком мониторе
 * сайдбар у левого края и «Выйти» у правого выглядели разбросанными.
 * Каркас один на все экраны вошедшего, а не только на табы: сайдбар
 * остаётся и в чате, и в настройках.
 *
 * На телефоне, планшете и в Telegram — просто страница, как раньше.
 */
export function DesktopShell() {
  const desktop = useDesktopLayout();
  useFullWidthPage(desktop);
  if (!desktop) return <Outlet />;

  return (
    <DesktopShellContext.Provider value>
      <DesktopRootsContext.Provider value={ROOTS}>
        <div className="min-h-dvh bg-glass text-ink">
          <div className="mx-auto flex w-full max-w-[1180px] gap-8 px-6">
            <Sidebar />
            <div className="relative h-dvh w-[640px] min-w-0 shrink bg-bg" style={COLUMN_STYLE}>
              <Outlet />
            </div>
            <RightRail />
          </div>
        </div>
      </DesktopRootsContext.Provider>
    </DesktopShellContext.Provider>
  );
}

function Sidebar() {
  const { t } = useTranslation('DesktopShell');
  const { t: tTabs } = useTranslation('TabBar');
  const { t: tProfile } = useTranslation('ProfilePage');
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  // Гость веб-версии пользуется приложением целиком (ленивая регистрация,
  // WEB.md): разделы те же, а на месте премиума — «Войти».
  const guest = user?.isGuest ?? false;
  const name = user?.displayName?.trim() || (guest ? t('guestName') : '');

  return (
    <aside className="sticky top-0 flex h-dvh w-[240px] shrink-0 flex-col py-7">
      <div className="flex items-center gap-3 px-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-bg text-lg font-bold text-signal-blue">
          {name ? name[0]!.toUpperCase() : '·'}
        </span>
        <span className="truncate text-base font-bold tracking-wide uppercase">{name}</span>
      </div>

      <nav
        aria-label={tTabs('navigation')}
        className="mt-8 flex min-h-0 flex-col gap-0.5 overflow-y-auto"
      >
        {MAIN_TABS.map(({ to, labelKey, icon: Icon }) => (
          <NavLink key={to} to={to} className={navItemClass}>
            <Icon className="size-6" strokeWidth={1.8} aria-hidden="true" />
            {tTabs(labelKey)}
          </NavLink>
        ))}

        {LEARNING_LINKS.map(({ to, labelKey, icon: Icon }) => (
          <NavLink key={to} to={to} className={navItemClass}>
            <Icon className="size-6" strokeWidth={1.8} aria-hidden="true" />
            {tProfile(labelKey)}
          </NavLink>
        ))}

        {/* Сразу под разделами учёбы, а не у низа окна (решение Дмитрия, 2026-10-26). */}
        {guest && (
          <Button
            fullWidth
            size="lg"
            variant="primaryBlue"
            icon={<LogIn className="size-5 shrink-0" aria-hidden="true" />}
            onClick={() => void navigate(ROUTES.login, { state: { reason: 'save' } })}
            className="mt-3 shrink-0 whitespace-nowrap"
          >
            {t('login')}
          </Button>
        )}
        {user && !user.isPremium && !guest && (
          <Button
            fullWidth
            size="lg"
            variant="primaryBlue"
            icon={<Crown className="size-5 shrink-0" aria-hidden="true" />}
            onClick={() => void navigate(ROUTES.premium)}
            className="mt-3 shrink-0 whitespace-nowrap"
          >
            {t('premium')}
          </Button>
        )}

        <div className="mx-4 my-3 h-px bg-hairline" aria-hidden="true" />

        {ACCOUNT_LINKS.filter((link) => !('premiumOnly' in link) || user?.isPremium).map((link) => {
          const Icon = link.icon;
          return 'to' in link ? (
            <NavLink key={link.labelKey} to={link.to} className={navItemClass}>
              <Icon className="size-6" strokeWidth={1.8} aria-hidden="true" />
              {tProfile(link.labelKey)}
            </NavLink>
          ) : (
            <button
              key={link.labelKey}
              type="button"
              onClick={() => openTelegram(OPERATOR.contact.href)}
              className={cn(NAV_ITEM, 'text-ink hover:bg-bg')}
            >
              <Icon className="size-6" strokeWidth={1.8} aria-hidden="true" />
              {tProfile(link.labelKey)}
            </button>
          );
        })}
      </nav>

    </aside>
  );
}

function RightRail() {
  const { t } = useTranslation('DesktopShell');
  const { user } = useCurrentUser();
  return (
    <div className="sticky top-0 h-dvh w-[160px] shrink-0 py-7">
      {/* Гостю выходить некуда: выход стёр бы его онбординг. */}
      {!user?.isGuest && (
        <button
          type="button"
          onClick={() => void logout()}
          className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-ink hover:bg-bg"
        >
          <LogOut className="size-5" strokeWidth={1.8} aria-hidden="true" />
          {t('logout')}
        </button>
      )}
    </div>
  );
}
